import React, {
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Modal,
  Select,
  Explainer,
  Paragraph,
  TextInput,
  InputButton,
  useNotification
} from '@sopranodesign/pulse-components';
import Icon from '@sopranodesign/pulse-icons';
import { useQueryClient } from '@sopranodesign/pulse-hooks';

import {
  resyncAll,
  connectZendesk,
  deleteCategory,
  resyncCategory,
  saveCategories,
  saveZendeskTags,
  createZendeskTag,
  fetchZendeskTags,
  disconnectZendesk,
  recrawlZendeskArticle,
  fetchZendeskAvailableTags
} from '../api';
import WarningMessageBody from './WarningMessageBody';
import TagModal from './TagModal';
import { useTagManager } from './useTagManager';

const ZendeskModalManager = ({
  activeModal,
  modalProps,
  closeModal,
  categories,
  setCategories,
  setErrorIds,
  setCategoryLoadingIds,
  setArticleLoadingIds,
  setIsSyncingAll
}) => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [subdomain, setSubdomain] = useState('');
  const [email, setEmail] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [showApiToken, setShowApiToken] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const {
    tags,
    setTags,
    selectedTags,
    setSelectedTags,
    handleTagChange,
  } = useTagManager({
    scope: 'generative_ai_zendesk_articles',
    createTagFn: createZendeskTag
  });

  const toggleApiTokenVisibility = () => setShowApiToken(prev => !prev);

  const handleConnect = async () => {
    if (!subdomain || !email || !apiToken) {
      setConnectError('Please fill in all fields before connecting to Zendesk.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setConnectError('Please enter a valid email address.');
      return;
    }
    setConnectError('');
    try {
      const response = await connectZendesk({ subdomain, username: email, token: apiToken });
      setCategories(response.data ?? []);
      showSuccess('Zendesk connect successfully!');
      closeModal();
    } catch (error) {
      setConnectError(
        error.response?.data?.errors
          ? Object.values(error.response.data.errors)[0][0]
          : 'Failed to connect.'
      );
    }
  };

  const handleSaveCategories = async () => {
    try {
      const payload = selectedCategories.map(cat => ({ id: cat.value, name: cat.label }));
      await saveCategories(payload);
      queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/'] });
      showSuccess('Articles fetched successfully!');
      closeModal();
      setSelectedCategories([]);
    } catch (error) {
      showError(`Failed to fetch articles: ${error.message}`);
    }
  };

  const confirmationActions = {
    delete: {
      header: 'Confirm Delete Category',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete the category "${modalProps.category?.name}"?`}
          contentBody="All synced articles will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        const categoryId = modalProps.category.id;
        // optional: mark category as loading during delete
        setCategoryLoadingIds(prev => ({ ...prev, [categoryId]: true }));
        try {
          await deleteCategory(categoryId);
          showSuccess(`Category "${modalProps.category.name}" delete requested`);
          // Invalidate list so removed category disappears when backend reports
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to delete category: ${error.message}`);
        } finally {
          setCategoryLoadingIds(prev => ({ ...prev, [categoryId]: false }));
        }
      }
    },
    disconnect: {
      header: 'Confirm Disconnect Zendesk',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to disconnect Zendesk?"
          contentBody="All synced categories and articles will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Disconnect',
      handleConfirm: async () => {
        try {
          await disconnectZendesk();
          showSuccess(`Zendesk disconnect requested`);
          // Invalidate root settings/categories
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to disconnect Zendesk: ${error.message}`);
        }
      }
    },
    resync: {
      header: 'Confirm Resync Category',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to resync the category “${modalProps.category?.name}”?`}
          contentBody="This will refresh its content."
        />
      ),
      confirmText: 'Resync',
      handleConfirm: async () => {
        const categoryId = modalProps.category.id;
        // NEW: mark loading for this category
        setCategoryLoadingIds(prev => ({ ...prev, [categoryId]: true }));
        try {
          await resyncCategory(categoryId);
          showSuccess(`Category resync requested.`);
          // Optional: invalidate category articles under this category if needed
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/articles', categoryId] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync category: ${error.message}`);
        } finally {
          setCategoryLoadingIds(prev => ({ ...prev, [categoryId]: false }));
        }
      }
    },
    resyncAll: {
      header: 'Confirm Resync All Categories',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to resync all categories?"
          contentBody="This will refresh all categories content."
        />
      ),
      confirmText: 'Resync All',
      handleConfirm: async () => {
        setIsSyncingAll(true);
        try {
          await resyncAll();
          showSuccess(`Categories resync requested.`);
          // Refresh lists
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync categories: ${error.message}`);
        } finally {
          setIsSyncingAll(false);
        }
      }
    },
    recrawl: {
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl the article “${modalProps.article?.title}”?`}
          contentBody="This will update its content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        const articleId = modalProps.article.id;
        const categoryId = modalProps.categoryId;

        // Clear any previous error for this article and mark loading
        setErrorIds(prev => {
          const next = { ...prev };
          delete next[articleId];
          return next;
        });
        setArticleLoadingIds(prev => ({ ...prev, [articleId]: true }));

        try {
          queryClient.setQueriesData({ queryKey: ['generative-ai/zendesk/articles', categoryId] }, (oldData) => {
            if (!oldData) { return oldData; }
            const cloned = JSON.parse(JSON.stringify(oldData));
            const list = cloned?.data?.data ?? cloned?.data ?? cloned?.data?.articles ?? [];
            const arr = Array.isArray(list) ? list : [];
            arr.forEach(a => {
              if (a.id === articleId) {
                a.status = 'Pending';
              }
            });
            // Attempt to set back in the same shape
            if (cloned?.data?.data) { cloned.data.data = arr; }
            else if (cloned?.data) { cloned.data = arr; }
            return cloned;
          });
        } catch {
          // If cache shape differs, we silently ignore; UI will still show spinner
        }

        try {
          await recrawlZendeskArticle(articleId);
          showSuccess(`Article recrawl requested`);
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/articles', categoryId] });
        } catch (error) {
          if (error.response?.status >= 500) {
            setErrorIds(prev => ({ ...prev, [articleId]: 'Failed to recrawl the article.' }));
          }
          showError(`Failed to recrawl article: ${error.message}`);
        } finally {
          setArticleLoadingIds(prev => ({ ...prev, [articleId]: false }));
          closeModal();
        }
      }
    }
  };

  useEffect(() => {
    const openAndLoadTags = async () => {
      if (activeModal === 'tags' && modalProps?.article?.id) {
        try {
          const availableResp = await fetchZendeskAvailableTags();
          const availableTags = availableResp?.data?.data ?? [];
          setTags(availableTags);

          const assignedResp = await fetchZendeskTags(modalProps.article.id);
          const tagList = assignedResp?.data?.data ?? [];
          const ids = tagList.map(t => t.id);

          setSelectedTags(ids);
          setIsTagModalOpen(true);
        } catch (error) {
          showError(error.message || 'Failed to load tags.');
          setIsTagModalOpen(true);
        }
      }
    };

    openAndLoadTags();

    // Cleanup when switching away or closing
    return () => {
      if (activeModal !== 'tags') {
        setSelectedTags([]);
        setIsTagModalOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, modalProps?.article?.id]);

  if (!activeModal) {
    return null;
  }

  switch (activeModal) {
    case 'connect':
      return (
        <Modal
          header="Connect Zendesk"
          handleCancel={closeModal}
          handleConfirm={handleConnect}
          confirmText="Connect"
          cancelText="Cancel"
          body={
            <Flex col gap="sm">
              {connectError && (
                <Explainer type="danger" mb hideLabel>
                  <Paragraph sm danger>{connectError}</Paragraph>
                </Explainer>
              )}
              <Flex fat col>
                <Label htmlFor="zendeskSubdomain">Zendesk Subdomain</Label>
                <TextInput
                  id="zendeskSubdomain"
                  value={subdomain}
                  onChange={e => setSubdomain(e.target.value)}
                />
              </Flex>
              <Flex fat col>
                <Label htmlFor="email">Email</Label>
                <TextInput id="email" value={email} onChange={e => setEmail(e.target.value)} />
              </Flex>
              <Flex fat col>
                <Label htmlFor="apiToken">API Token</Label>
                <Flex relative>
                  <TextInput
                    id="apiToken"
                    type={showApiToken ? 'text' : 'password'}
                    value={apiToken}
                    onChange={e => setApiToken(e.target.value)}
                  />
                  <InputButton
                    type="button"
                    aria-label={`${showApiToken ? 'hide' : 'show'} api token`}
                    onClick={toggleApiTokenVisibility}
                  >
                    <Icon
                      size="1.25rem"
                      width="1.25rem"
                      height="1.25rem"
                      type={showApiToken ? 'eye' : 'eyeOff'}
                    />
                  </InputButton>
                </Flex>
              </Flex>
            </Flex>
          }
        />
      );

    case 'category':
      return (
        <Modal
          header="Help Center Categories"
          handleCancel={closeModal}
          handleConfirm={handleSaveCategories}
          confirmText="Select"
          cancelText="Cancel"
          disableConfirmButton={selectedCategories.length === 0}
          body={
            <Flex col gap="sm">
              <Label htmlFor="categorySelect">Category</Label>
              <Select
                id="categorySelect"
                value={selectedCategories}
                onChange={setSelectedCategories}
                options={categories.map(cat => ({ label: cat.name, value: cat.id }))}
                placeholder="Select a category"
                isMulti
              />
            </Flex>
          }
        />
      );

    case 'tags':
      return (
        <TagModal
          isVisible={isTagModalOpen}
          header="Zendesk Article Tags"
          onCancel={() => {
            setIsTagModalOpen(false);
            setSelectedTags([]);
            closeModal();
          }}
          onConfirm={async () => {
            try {
              await saveZendeskTags({
                zendeskId: modalProps.article.id,
                groupIds: selectedTags
              });

              // Keep cache in sync for the article list under the category
              queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/articles', modalProps.categoryId] });

              setIsTagModalOpen(false);
              setSelectedTags([]);
              showSuccess('Tags assigned successfully!');
              closeModal();
            } catch (error) {
              showError(error.message || 'Failed to assign tags.');
            }
          }}
          tags={tags}
          selectedTags={selectedTags}
          handleTagChange={handleTagChange}
        />
      );

    case 'confirmation': {
      const config = confirmationActions[modalProps.type];
      return (
        <Modal
          header={config.header}
          body={config.body}
          cancelText="Cancel"
          confirmText={config.confirmText}
          handleCancel={closeModal}
          handleConfirm={config.handleConfirm}
        />
      );
    }

    default:
      return null;
  }
};

ZendeskModalManager.propTypes = {
  activeModal: PropTypes.string,
  modalProps: PropTypes.shape({
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    category: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    }),
    article: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string
    })
  }),
  closeModal: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    })
  ).isRequired,
  setCategories: PropTypes.func.isRequired,
  setErrorIds: PropTypes.func.isRequired,
  setCategoryLoadingIds: PropTypes.func.isRequired,
  setArticleLoadingIds: PropTypes.func.isRequired,
  setIsSyncingAll: PropTypes.func.isRequired
};

export default ZendeskModalManager;